import * as React from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { cn } from '@noor/lib/cn';
import { Button } from './button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './dialog';

export interface FiltersButtonProps extends Omit<
  React.ComponentProps<'button'>,
  'children'
> {
  /** Accessible label (also used as the native tooltip). */
  label?: string;
}

/**
 * The round 40px filters trigger from the Figma title row — white circle with
 * a sliders glyph. Pages render it in the `PageHeader` actions slot, ahead of
 * the create/add button when the page has one, and open a `FiltersModal`
 * from it.
 */
export function FiltersButton({
  className,
  label = 'Filters',
  type = 'button',
  ...props
}: FiltersButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      data-slot="filters-button"
      className={cn(
        'flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-[var(--button-radius)] border border-[#e5e7eb] bg-white text-foreground shadow-xs transition-colors outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/40 dark:border-border dark:bg-card',
        className,
      )}
      {...props}
    >
      <SlidersHorizontal className="size-5" aria-hidden="true" />
    </button>
  );
}

/**
 * One labeled row inside a `FiltersModal` — the Figma Label-above-pill-input
 * pattern. The control keeps its own accessible name; this is the visible
 * caption only.
 */
export function FiltersField({
  label,
  children,
  className,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-[var(--filters-field-gap)]', className)}>
      <span
        data-slot="filters-field-label"
        className="text-[length:var(--form-label-font-size)] font-[var(--form-label-font-weight)] text-foreground"
      >
        {label}
      </span>
      {children}
    </div>
  );
}

export interface FiltersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Modal heading — the Figma "Фильтр" title. */
  title: React.ReactNode;
  /** Filter fields, stacked vertically (one labeled pill per row). */
  children: React.ReactNode;
  /** Primary action label (the orange pill). Defaults to closing the modal. */
  applyLabel: string;
  /**
   * Secondary action label (the gray pill). Rendered only when `onReset` is
   * provided — it clears the filters while the modal stays open.
   */
  resetLabel?: string;
  onApply?: () => void;
  onReset?: () => void;
  className?: string;
  bodyClassName?: string;
  /** Render secondary actions before Apply. Defaults to the existing order. */
  applyLast?: boolean;
}

/**
 * The Figma "Фильтр" dialog: title, stacked filter fields, and an
 * Apply/Reset footer pinned to the trailing (right) end. Filter controls
 * stage their edits locally and commit through the caller's `onApply`;
 * Reset restores the defaults via the caller's `onReset` while the modal
 * stays open.
 */
export function FiltersModal({
  open,
  onOpenChange,
  title,
  children,
  applyLabel,
  resetLabel,
  onApply,
  onReset,
  className,
  bodyClassName,
  applyLast = false,
}: FiltersModalProps) {
  const handleApply = () => {
    if (onApply) {
      onApply();
    } else {
      onOpenChange(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
    event.preventDefault();
    handleApply();
  };

  const resetAction =
    resetLabel && onReset ? (
      <Button type="button" variant="soft" shape="rounded" size="modal" onClick={onReset}>
        {resetLabel}
      </Button>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        focusOnOpen={false}
        className={cn(
          'gap-[var(--filters-modal-gap)] p-[var(--filters-modal-padding)] sm:max-w-[var(--filters-modal-max-width)]',
          className,
        )}
        onKeyDown={handleKeyDown}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {/* Field rows are form items — they follow the shared form row gap. */}
        <div className={cn('flex flex-col gap-[var(--form-gap-y)]', bodyClassName)}>
          {children}
        </div>
        <DialogFooter>
          {applyLast ? resetAction : null}
          <Button
            type="button"
            variant="dark"
            shape="rounded"
            size="modal"
            onClick={handleApply}
          >
            {applyLabel}
          </Button>
          {applyLast ? null : resetAction}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
